// Per-integration admin page. Reached from /integrations.
//
// Surfaces:
//   - Webhook URL + provider-specific secret/setup notes (always)
//   - For rules-mode: the routing rules editor + default-service picker
//   - Delete integration (always)
//
// Fixed-mode integrations don't expose a rules editor — that's the
// whole point of the mode. To migrate a fixed integration to rules,
// admin deletes and recreates (rare operation, keeps doc shape clean).

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { ArrowLeft, Copy, Eye, EyeOff, KeyRound, Plug, Trash2 } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import { orgIntegrationDoc, orgServicesCol } from '../lib/firestore';
import {
  PROVIDER_LABELS,
  deleteIntegration,
  readIntegration,
  updateIntegrationRouting,
  updateIntegrationSecret,
  webhookUrl,
} from '../lib/integrations';
import type {
  Integration,
  IntegrationProvider,
  RoutingMode,
  Service,
} from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';
import { RoutingRulesEditor } from '../components/integrations/RoutingRulesEditor';

export default function IntegrationDetailPage() {
  const { integrationId = '' } = useParams<{ integrationId: string }>();
  const { orgId } = useOrg();
  const navigate = useNavigate();

  const [integration, setIntegration] = useState<Integration | null | 'missing'>(null);
  const [services, setServices] = useState<Service[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!orgId || !integrationId) return;
    const unsubInt = onSnapshot(
      orgIntegrationDoc(orgId, integrationId),
      (snap) => {
        if (!snap.exists()) {
          setIntegration('missing');
          return;
        }
        setIntegration(
          readIntegration(snap.data() as Parameters<typeof readIntegration>[0]),
        );
      },
      (err) => {
        console.error('[integration-detail] snapshot failed', err);
        setError('Could not load integration.');
      },
    );
    const unsubServices = onSnapshot(
      query(orgServicesCol(orgId), orderBy('name', 'asc')),
      (snap) => setServices(snap.docs.map((d) => d.data() as Service)),
    );
    return () => {
      unsubInt();
      unsubServices();
    };
  }, [orgId, integrationId]);

  if (!orgId) return null;

  if (integration === 'missing') {
    return (
      <AppShell>
        <Container width="base">
          <PageHeader title="Integration not found" />
          <Link to="/integrations" className="ds-button ds-button--secondary">
            <ArrowLeft size={14} strokeWidth={1.75} />
            Back to integrations
          </Link>
        </Container>
      </AppShell>
    );
  }

  if (integration === null) {
    return (
      <AppShell>
        <Container width="base">
          <div style={{ padding: 'var(--space-8) 0' }}>
            <Row gap={2} align="center">
              <Spinner size="sm" />
              <span className="ds-text-muted ds-text-sm">Loading…</span>
            </Row>
          </div>
        </Container>
      </AppShell>
    );
  }

  const mode: RoutingMode = integration.routingMode ?? 'fixed';

  async function doDelete() {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteIntegration(orgId!, integrationId);
      navigate('/integrations');
    } catch (err) {
      console.error('[integration-detail] delete failed', err);
      setError(extractMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title={integration.name}
          subtitle={`${PROVIDER_LABELS[integration.provider]} · ${
            mode === 'fixed' ? 'Single service' : 'Rules-based routing'
          }`}
          actions={
            <Link to="/integrations" className="ds-button ds-button--ghost ds-button--sm">
              <ArrowLeft size={12} strokeWidth={2} />
              All integrations
            </Link>
          }
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={6}>
          <WebhookPanel
            orgId={orgId}
            integrationId={integration.id}
            provider={integration.provider}
            secret={integration.secret}
          />

          {mode === 'rules' && services && (
            <Card>
              <CardBody>
                <RoutingRulesEditor
                  provider={integration.provider}
                  services={services}
                  initialRules={integration.routingRules ?? []}
                  initialDefaultServiceId={integration.defaultServiceId}
                  onSave={async ({ rules, defaultServiceId }) => {
                    await updateIntegrationRouting(orgId, integration.id, {
                      routingRules: rules,
                      defaultServiceId,
                    });
                  }}
                />
              </CardBody>
            </Card>
          )}

          {mode === 'fixed' && (
            <Card>
              <CardBody>
                <Stack gap={2}>
                  <span style={{ fontWeight: 'var(--fw-medium)' }}>Routing</span>
                  <span className="ds-text-sm">
                    <span className="ds-text-muted">All alerts → </span>
                    <Link
                      to={`/services/${integration.serviceId}`}
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      {integration.serviceName || integration.serviceId}
                    </Link>
                  </span>
                  <span className="ds-text-tertiary ds-text-xs">
                    To switch this integration to rule-based routing, delete it and create a new
                    one in rules mode &mdash; keeps the data model clean.
                  </span>
                </Stack>
              </CardBody>
            </Card>
          )}

          <RotateSecretCard
            orgId={orgId}
            integrationId={integration.id}
            provider={integration.provider}
          />

          <Card>
            <CardBody>
              <Row gap={3} justify="between" align="center">
                <Stack gap={1}>
                  <span style={{ fontWeight: 'var(--fw-medium)' }}>Delete integration</span>
                  <span className="ds-text-muted ds-text-sm">
                    The webhook URL stops working immediately. Remove or rotate it on the provider
                    side too.
                  </span>
                </Stack>
                <Button
                  variant="danger"
                  size="sm"
                  iconLeft={<Trash2 size={12} strokeWidth={2} />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              </Row>
            </CardBody>
          </Card>
        </Stack>

        <ConfirmDialog
          open={confirmDelete}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
          title="Delete this integration?"
          confirmLabel="Delete"
          destructive
          loading={deleting}
        >
          <p>
            <strong>{integration.name}</strong> will stop receiving alerts. This can&apos;t be
            undone &mdash; you&apos;ll need to recreate the integration and reconfigure the
            provider.
          </p>
        </ConfirmDialog>
      </Container>
    </AppShell>
  );
}

function WebhookPanel({
  orgId,
  integrationId,
  provider,
  secret,
}: {
  orgId: string;
  integrationId: string;
  provider: IntegrationProvider;
  secret: string;
}) {
  const url = webhookUrl(provider, orgId, integrationId);
  const [urlCopied, setUrlCopied] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  // Sentry's Client Secret is only ever shown once on Sentry's side, so
  // losing it = recreate the integration there. We keep our own copy
  // (we have to, for HMAC verification) and let admins reveal it on
  // demand. Default-hidden because admins screenshare these screens.
  const [sentrySecretRevealed, setSentrySecretRevealed] = useState(false);

  function copy(text: string, set: (b: boolean) => void) {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        set(true);
        setTimeout(() => set(false), 1500);
      })
      .catch(() => undefined);
  }

  return (
    <Card>
      <CardBody>
        <Stack gap={4}>
          <Row gap={2} align="center">
            <Plug size={14} strokeWidth={2} color="var(--color-accent)" />
            <span style={{ fontWeight: 'var(--fw-semibold)' }}>Webhook configuration</span>
            <Badge tone="accent">{PROVIDER_LABELS[provider]}</Badge>
          </Row>

          <Stack gap={1}>
            <span className="ds-label">Webhook URL</span>
            <Row gap={2} align="center">
              <code
                className="ds-mono"
                style={{
                  flex: 1,
                  wordBreak: 'break-all',
                  background: 'rgba(0,0,0,0.25)',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--fs-xs)',
                }}
              >
                {url}
              </code>
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Copy size={12} strokeWidth={2} />}
                onClick={() => copy(url, setUrlCopied)}
              >
                {urlCopied ? 'Copied' : 'Copy'}
              </Button>
            </Row>
          </Stack>

          {provider === 'sentry' ? (
            <Stack gap={1}>
              <Row gap={2} align="center" justify="between">
                <span className="ds-label">Sentry Client Secret</span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={
                    sentrySecretRevealed
                      ? <EyeOff size={12} strokeWidth={2} />
                      : <Eye size={12} strokeWidth={2} />
                  }
                  onClick={() => setSentrySecretRevealed((v) => !v)}
                >
                  {sentrySecretRevealed ? 'Hide' : 'Reveal'}
                </Button>
              </Row>
              <Row gap={2} align="center">
                <code
                  className="ds-mono"
                  style={{
                    flex: 1,
                    wordBreak: 'break-all',
                    background: 'rgba(0,0,0,0.25)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--fs-xs)',
                    letterSpacing: sentrySecretRevealed ? 0 : '0.15em',
                  }}
                >
                  {sentrySecretRevealed ? secret : '•'.repeat(Math.min(secret.length, 40))}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<Copy size={12} strokeWidth={2} />}
                  onClick={() => copy(secret, setSecretCopied)}
                >
                  {secretCopied ? 'Copied' : 'Copy'}
                </Button>
              </Row>
              <span className="ds-text-tertiary ds-text-xs">
                Sentry only displays this once on their side. Rouser keeps a copy here so you can
                recover it without recreating the integration in Sentry.
              </span>
            </Stack>
          ) : (
            <Stack gap={1}>
              <span className="ds-label">Header — X-Rouser-Secret</span>
              <Row gap={2} align="center">
                <code
                  className="ds-mono"
                  style={{
                    flex: 1,
                    wordBreak: 'break-all',
                    background: 'rgba(0,0,0,0.25)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--fs-xs)',
                  }}
                >
                  {secret}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<Copy size={12} strokeWidth={2} />}
                  onClick={() => copy(secret, setSecretCopied)}
                >
                  {secretCopied ? 'Copied' : 'Copy'}
                </Button>
              </Row>
            </Stack>
          )}

          <p className="ds-text-tertiary ds-text-xs" style={{ marginTop: 'var(--space-2)' }}>
            {providerInstructions(provider)}
          </p>
        </Stack>
      </CardBody>
    </Card>
  );
}

/**
 * Lets the admin rotate the integration's secret in place — useful when
 * Sentry's Client Secret has been rotated on Sentry's side and we need
 * to keep our stored copy in sync. For non-Sentry providers, hitting
 * "Generate new" mints a fresh random token; the admin then has to
 * update the X-Rouser-Secret header on the provider side.
 *
 * The new secret is shown once after rotation in the same copy panel
 * style as the create flow. Default-collapsed because rotating is rare
 * and we don't want to draw attention to it during normal use.
 */
function RotateSecretCard({
  orgId,
  integrationId,
  provider,
}: {
  orgId: string;
  integrationId: string;
  provider: IntegrationProvider;
}) {
  const [open, setOpen] = useState(false);
  const [pasted, setPasted] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freshSecret, setFreshSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isSentry = provider === 'sentry';

  async function rotate() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await updateIntegrationSecret(orgId, integrationId, {
        secret: isSentry ? pasted.trim() : undefined,
      });
      setFreshSecret(res.secret);
      setPasted('');
    } catch (err) {
      console.error('[rotate] failed', err);
      setError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function copy(text: string) {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  }

  return (
    <Card>
      <CardBody>
        <Stack gap={3}>
          <Row gap={3} justify="between" align="center">
            <Stack gap={1}>
              <span style={{ fontWeight: 'var(--fw-medium)' }}>Rotate secret</span>
              <span className="ds-text-muted ds-text-sm">
                {isSentry
                  ? 'Use this when you rotate the Client Secret on Sentry\'s side, so Rouser keeps verifying signatures correctly.'
                  : 'Generates a fresh X-Rouser-Secret. After rotation, update the provider\'s webhook header — the old value stops working immediately.'}
              </span>
            </Stack>
            {!open && !freshSecret && (
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<KeyRound size={12} strokeWidth={2} />}
                onClick={() => setOpen(true)}
              >
                Rotate
              </Button>
            )}
          </Row>

          {open && !freshSecret && (
            <Stack gap={3}>
              {isSentry && (
                <FormField
                  label="New Sentry Client Secret"
                  hint="Paste the rotated secret from Sentry → Settings → Custom Integrations."
                >
                  {(id) => (
                    <Input
                      id={id}
                      type="password"
                      value={pasted}
                      onChange={(e) => setPasted(e.target.value)}
                      placeholder="•••• rotated Client Secret"
                      autoFocus
                    />
                  )}
                </FormField>
              )}
              {error && (
                <div
                  style={{
                    padding: 'var(--space-3) var(--space-4)',
                    background: 'var(--color-danger-soft)',
                    color: 'var(--color-danger-text)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-danger)',
                    fontSize: 'var(--fs-sm)',
                  }}
                >
                  {error}
                </div>
              )}
              <Row gap={2} justify="end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setOpen(false);
                    setPasted('');
                    setError(null);
                  }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={rotate}
                  loading={submitting}
                  disabled={isSentry ? !pasted.trim() : false}
                >
                  {isSentry ? 'Save new secret' : 'Generate new secret'}
                </Button>
              </Row>
            </Stack>
          )}

          {freshSecret && !isSentry && (
            <Stack gap={2}>
              <span className="ds-label">New X-Rouser-Secret</span>
              <Row gap={2} align="center">
                <code
                  className="ds-mono"
                  style={{
                    flex: 1,
                    wordBreak: 'break-all',
                    background: 'rgba(0,0,0,0.25)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--fs-xs)',
                  }}
                >
                  {freshSecret}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<Copy size={12} strokeWidth={2} />}
                  onClick={() => copy(freshSecret)}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Row>
              <span className="ds-text-tertiary ds-text-xs">
                Update this on the provider side now — the old value is no longer valid.
              </span>
              <Row justify="end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFreshSecret(null);
                    setOpen(false);
                  }}
                >
                  Done
                </Button>
              </Row>
            </Stack>
          )}

          {freshSecret && isSentry && (
            <Stack gap={2}>
              <Badge tone="success" dot>
                Sentry Client Secret updated
              </Badge>
              <span className="ds-text-tertiary ds-text-xs">
                Next webhook from Sentry will verify against the new value.
              </span>
              <Row justify="end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFreshSecret(null);
                    setOpen(false);
                  }}
                >
                  Done
                </Button>
              </Row>
            </Stack>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}

function providerInstructions(provider: IntegrationProvider): string {
  switch (provider) {
    case 'sentry':
      return 'Paste this URL into your Sentry Internal Integration as the Webhook URL. Sentry signs each webhook with the Client Secret you supplied at creation; Rouser verifies the HMAC signature on receipt.';
    default:
      return "Paste both into the provider's webhook configuration. Send the secret in the X-Rouser-Secret header. Treat the secret like a password — anyone with it can fire incidents into your org.";
  }
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
